import { type PropsWithChildren, useCallback, useRef, useState } from 'react';
import { InfoIcon } from 'lucide-react';

import { navigateToStripeManagement } from '~/features/subscriptions/components/Subscribe/helpers/navigateToStripeManagement.ts';
import { type Repub } from '~/vendor/cx-modules/types';
import { type Subscription } from '~/vendor/cx-web/domain-types/myoc';
import { logout } from '~/vendor/cx-web/feature-auth';
import { useAuthModal, VIEW_LOGIN } from '~/vendor/cx-web/feature-myoc-login/auth-modal-provider';
import { trackEvent, TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS } from '~/vendor/cx-web/feature-tracking';
import { getRepubMetaFromRepub } from '~/vendor/cx-web/utils';
import { Button } from '~/vendor/cxy/components/Button';
import { Dialog } from '~/vendor/cxy/components/Dialog';
import { Popover } from '~/vendor/cxy/components/Popover';
import { AccountIcon } from '~/vendor/cxy/icons/glyphs/components/product-plus/AccountIcon';

import styles from './styles/Menu.module.css';

const AccountButton = ({ children }: PropsWithChildren) => (
  <>
    <AccountIcon />
    <span>{children}</span>
  </>
);

const MenuList = ({
  isLoggedIn,
  prosperStackUrl,
  repub,
  subscriptions,
}: {
  isLoggedIn: boolean;
  prosperStackUrl: string;
  repub: Repub;
  subscriptions?: Subscription[];
}) => {
  const { openAtView } = useAuthModal();
  const { repubIs, repubPath } = getRepubMetaFromRepub(repub);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const openAuthenticationModal = () => {
    // WS lands on the plus landing page after logout (SPOR-2292 AC) rather than staying on the
    // current page, which is likely a premium tool the logged-out user can no longer use. Other
    // repubs keep the stay-on-page behaviour unchanged.
    const postLogoutUrl = repubIs.WS
      ? `${repubPath}/plus`
      : window.location.pathname + window.location.search + window.location.hash;
    isLoggedIn ? logout(repub, postLogoutUrl) : openAtView(VIEW_LOGIN);

    const event = isLoggedIn ? TRACKING_EVENTS.LOG_OUT : TRACKING_EVENTS.LOGIN;
    trackEvent(event, {
      eventAction: TRACKING_ACTIONS[event],
      eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
      eventLabel: `My Account Log ${isLoggedIn ? 'out' : 'in'}`,
    });
  };

  const hasSubscription = subscriptions && subscriptions.length > 0;
  const hasStripeSubscription =
    hasSubscription && subscriptions?.some(subscription => subscription.provider === 'STRIPE');

  const navigateToProsperStack = useCallback(async () => {
    window.open(prosperStackUrl, '_blank');
  }, [prosperStackUrl]);

  const handleManageSubscription = useCallback(() => {
    if (!hasStripeSubscription) {
      setIsDialogOpen(true);
      return;
    }

    if (repubIs.WS) {
      navigateToStripeManagement({ repub });
    } else {
      navigateToProsperStack();
    }
  }, [hasStripeSubscription, navigateToProsperStack, setIsDialogOpen, repub, repubIs.WS]);

  return (
    <>
      {isLoggedIn ? (
        <>
          <Popover>
            <Popover.Trigger aria-label="Toggle account menu" className={styles.trigger}>
              <AccountButton>Account</AccountButton>
            </Popover.Trigger>
            <Popover.Content>
              <ul>
                {hasSubscription && (
                  <li className={styles.menuItem}>
                    <Button key="manage-subs" onClick={handleManageSubscription} ref={triggerRef} variant="ghost">
                      Manage Subscriptions
                    </Button>
                  </li>
                )}
                <li className={styles.menuItem}>
                  <Button key="log-in" onClick={openAuthenticationModal} variant="ghost">
                    {isLoggedIn ? 'Log out' : 'Log in'}
                  </Button>
                </li>
              </ul>
            </Popover.Content>
          </Popover>
          <Dialog
            className={styles.manageSubsDialog}
            isOpen={isDialogOpen}
            setIsOpen={setIsDialogOpen}
            title="Manage Subscription Alert"
            triggerRef={triggerRef}
          >
            <div>
              <h2>
                <InfoIcon />
                To Unsubscribe
              </h2>
              <p>
                To manage your subscription, please visit the Settings app on your device and follow the required steps.
              </p>
              <div className={styles.buttonWrapper}>
                <Button className={styles.closeDialog} onClick={() => setIsDialogOpen(false)} variant="secondary">
                  Close
                </Button>
              </div>
            </div>
          </Dialog>
        </>
      ) : (
        <Button className={styles.trigger} onClick={openAuthenticationModal} variant="ghost">
          <AccountButton>{isLoggedIn ? 'Log out' : 'Log in'}</AccountButton>
        </Button>
      )}
    </>
  );
};

export { MenuList };
